import { useCallback, useEffect, useRef, useState } from 'react';
import { authClient } from '@/lib/auth-client';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  messageId?: string;
}

interface WebSocketMessage {
  type: string;
  content?: string;
  messageId?: string;
  timestamp: Date | string;
  messages?: ChatMessage[];
  data?: any;
}

interface UseWebSocketOptions {
  url: string;
  reconnectAttempts?: number;
  reconnectInterval?: number;
}

export function useWebSocket({
  url,
  reconnectAttempts = 3,
  reconnectInterval = 3000,
}: UseWebSocketOptions) {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>(null);
  const { data: session } = authClient.useSession({});

  const connect = useCallback(() => {
    if (socket?.readyState === WebSocket.OPEN) {
      return;
    }

    setIsConnecting(true);
    setLastError(null);

    const ws = new WebSocket(url);

    ws.onopen = () => {
      console.log('WebSocket connected');
      setIsConnected(true);
      setIsConnecting(false);
      reconnectAttemptsRef.current = 0;

      // Authenticate immediately after connection
      if (session?.user?.id) {
        ws.send(
          JSON.stringify({
            type: 'auth',
            data: {
              userId: session.user.id,
              sessionToken: 'temp_token', // In production, use actual session token
            },
          })
        );
      }
    };

    ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);

        switch (message.type) {
          case 'auth_success':
            console.log('WebSocket authenticated');
            if (message.content) {
              setMessages((prev) => [
                ...prev,
                {
                  id: `system_${Date.now()}`,
                  role: 'system' as const,
                  content: message.content as string,
                  timestamp: new Date(message.timestamp),
                },
              ]);
            }
            break;

          case 'assistant_message':
            setIsTyping(false);
            if (message.content) {
              setMessages((prev) => [
                ...prev,
                {
                  id: message.messageId || `assistant_${Date.now()}`,
                  role: 'assistant' as const,
                  content: message.content as string,
                  timestamp: new Date(message.timestamp),
                },
              ]);
            }
            break;

          case 'chat_history':
            if (message.messages) {
              setMessages(
                message.messages.map((msg) => ({
                  ...msg,
                  timestamp: new Date(msg.timestamp),
                }))
              );
            }
            break;

          case 'typing':
            setIsTyping(true);
            break;

          case 'analysis_result':
          case 'insights_result':
            setIsTyping(false);
            if (message.content) {
              setMessages((prev) => [
                ...prev,
                {
                  id: `analysis_${Date.now()}`,
                  role: 'assistant' as const,
                  content: message.content as string,
                  timestamp: new Date(message.timestamp),
                },
              ]);
            }
            break;

          case 'error':
            setIsTyping(false);
            setLastError(message.content || 'Unknown error occurred');
            break;

          case 'pong':
            // Keep-alive response
            break;

          default:
            console.log('Unknown message type:', message.type);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onclose = (event) => {
      console.log('WebSocket disconnected:', event.code, event.reason);
      setIsConnected(false);
      setIsConnecting(false);
      setIsTyping(false);

      // Attempt to reconnect
      if (reconnectAttemptsRef.current < reconnectAttempts) {
        reconnectAttemptsRef.current++;
        console.log(
          `Attempting to reconnect... (${reconnectAttemptsRef.current}/${reconnectAttempts})`
        );

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, reconnectInterval);
      } else {
        setLastError('Connection lost. Please refresh the page to reconnect.');
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setLastError('Connection error occurred');
      setIsConnecting(false);
    };

    setSocket(ws);
  }, [url, session?.user?.id, reconnectAttempts, reconnectInterval]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    if (socket) {
      socket.close();
      setSocket(null);
    }

    setIsConnected(false);
    setIsConnecting(false);
    setIsTyping(false);
  }, [socket]);

  const sendMessage = useCallback(
    (content: string) => {
      if (socket?.readyState === WebSocket.OPEN && content.trim()) {
        // Add user message immediately to UI
        const userMessage: ChatMessage = {
          id: `user_${Date.now()}`,
          role: 'user',
          content: content.trim(),
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, userMessage]);

        // Send to server
        socket.send(
          JSON.stringify({
            type: 'chat',
            data: { content: content.trim() },
          })
        );

        return true;
      }
      return false;
    },
    [socket]
  );

  const requestAnalysis = useCallback(
    (item: string, amount: number) => {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(
          JSON.stringify({
            type: 'analysis',
            data: { item, amount },
          })
        );
        return true;
      }
      return false;
    },
    [socket]
  );

  const requestInsights = useCallback(() => {
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(
        JSON.stringify({
          type: 'insights',
          data: {},
        })
      );
      return true;
    }
    return false;
  }, [socket]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const ping = useCallback(() => {
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'ping' }));
    }
  }, [socket]);

  // Auto-connect when session is available
  useEffect(() => {
    if (session?.user?.id && !socket) {
      connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [session?.user?.id, connect, socket]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  // Keep-alive ping every 30 seconds
  useEffect(() => {
    if (isConnected) {
      const pingInterval = setInterval(ping, 30_000);
      return () => clearInterval(pingInterval);
    }
  }, [isConnected, ping]);

  return {
    isConnected,
    isConnecting,
    isTyping,
    messages,
    lastError,
    sendMessage,
    requestAnalysis,
    requestInsights,
    clearMessages,
    connect,
    disconnect,
  };
}
